import React from 'react';
import { getAvailableThemes, getThemeDefaultHighlightColor } from 'components/quote_builder_widget/themes';
import { fieldGroupStyle, labelStyle, helpTextStyle, sectionHeaderStyle } from './sharedStyles';

const DEFAULT_BACKGROUND_COLOR = '#ffffff';

const FONT_FAMILIES = [
  { value: '', label: 'Default (System)' },
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Helvetica, Arial, sans-serif', label: 'Helvetica' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: 'Tahoma, sans-serif', label: 'Tahoma' },
  { value: '"Trebuchet MS", sans-serif', label: 'Trebuchet MS' },
  { value: '"Times New Roman", serif', label: 'Times New Roman' },
  { value: '"Courier New", monospace', label: 'Courier New' },
  { value: 'system-ui, sans-serif', label: 'System UI' },
];

const AppearanceSection = ({
  selectedTheme,
  setSelectedTheme,
  highlightColor,
  setHighlightColor,
  submitButtonColor,
  setSubmitButtonColor,
  backgroundColor,
  setBackgroundColor,
  fontFamily,
  setFontFamily,
  customCss,
  supportsCustomCss,
  onOpenCssEditor,
}) => {
  const availableThemes = getAvailableThemes();

  const colorInputContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  };

  const colorPreviewStyle = (color) => ({
    width: '40px',
    height: '40px',
    borderRadius: '6px',
    border: '2px solid #dee2e6',
    backgroundColor: color,
    cursor: 'pointer',
  });

  return (
    <div>
      <h4 style={sectionHeaderStyle}>
        {t('quote_builder_widget.section_appearance')}
      </h4>

      {/* Theme */}
      <div style={fieldGroupStyle}>
        <label style={labelStyle}>{t('quote_builder_widget.theme')}</label>
        <p style={helpTextStyle}>{t('quote_builder_widget.theme_info')}</p>
        <select
          className="form-control"
          value={selectedTheme}
          onChange={(event) => setSelectedTheme(event.target.value)}
          style={{ width: '250px' }}
        >
          {availableThemes.map((themeName) => (
            <option key={themeName} value={themeName}>
              {themeName.charAt(0).toUpperCase() + themeName.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {supportsCustomCss && (
        <>
          {/* Highlight Color */}
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>{t('quote_builder_widget.highlight_color')}</label>
            <p style={helpTextStyle}>{t('quote_builder_widget.highlight_color_info')}</p>
            <div style={colorInputContainerStyle}>
              <input
                type="color"
                value={highlightColor}
                onChange={(event) => setHighlightColor(event.target.value)}
                style={{ width: '50px', height: '40px', padding: '2px', cursor: 'pointer' }}
              />
              <input
                type="text"
                className="form-control"
                value={highlightColor}
                onChange={(event) => setHighlightColor(event.target.value)}
                style={{ width: '120px', fontFamily: 'monospace' }}
              />
              {highlightColor !== getThemeDefaultHighlightColor(selectedTheme) && (
                <button
                  type="button"
                  onClick={() => setHighlightColor(getThemeDefaultHighlightColor(selectedTheme))}
                  style={{
                    padding: '6px 10px',
                    fontSize: '12px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                  }}
                  title={t('quote_builder_widget.reset_to_theme_default')}
                >
                  {t('quote_builder_widget.reset')}
                </button>
              )}
            </div>
          </div>

          {/* Submit Button Color */}
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>{t('quote_builder_widget.submit_button_color')}</label>
            <p style={helpTextStyle}>{t('quote_builder_widget.submit_button_color_info')}</p>
            <div style={colorInputContainerStyle}>
              <input
                type="color"
                value={submitButtonColor || highlightColor}
                onChange={(event) => setSubmitButtonColor(event.target.value)}
                style={{ width: '50px', height: '40px', padding: '2px', cursor: 'pointer' }}
              />
              <input
                type="text"
                className="form-control"
                value={submitButtonColor || highlightColor}
                onChange={(event) => setSubmitButtonColor(event.target.value)}
                placeholder={highlightColor}
                style={{ width: '120px', fontFamily: 'monospace' }}
              />
              {submitButtonColor && (
                <button
                  type="button"
                  onClick={() => setSubmitButtonColor('')}
                  style={{
                    padding: '6px 10px',
                    fontSize: '12px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                  }}
                  title={t('quote_builder_widget.reset_to_highlight_color')}
                >
                  {t('quote_builder_widget.reset')}
                </button>
              )}
            </div>
          </div>

          {/* Background Color */}
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>{t('quote_builder_widget.background_color')}</label>
            <p style={helpTextStyle}>{t('quote_builder_widget.background_color_info')}</p>
            <div style={colorInputContainerStyle}>
              <input
                type="color"
                value={backgroundColor}
                onChange={(event) => setBackgroundColor(event.target.value)}
                style={{ width: '50px', height: '40px', padding: '2px', cursor: 'pointer' }}
              />
              <input
                type="text"
                className="form-control"
                value={backgroundColor}
                onChange={(event) => setBackgroundColor(event.target.value)}
                style={{ width: '120px', fontFamily: 'monospace' }}
              />
              {backgroundColor !== DEFAULT_BACKGROUND_COLOR && (
                <button
                  type="button"
                  onClick={() => setBackgroundColor(DEFAULT_BACKGROUND_COLOR)}
                  style={{
                    padding: '6px 10px',
                    fontSize: '12px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                  }}
                  title={t('quote_builder_widget.reset_to_default')}
                >
                  {t('quote_builder_widget.reset')}
                </button>
              )}
            </div>
          </div>

          {/* Font Family */}
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>{t('quote_builder_widget.font_family')}</label>
            <p style={helpTextStyle}>{t('quote_builder_widget.font_family_info')}</p>
            <select
              className="form-control"
              value={fontFamily}
              onChange={(event) => setFontFamily(event.target.value)}
              style={{ width: '250px' }}
            >
              {FONT_FAMILIES.map((font) => (
                <option key={font.value} value={font.value} style={{ fontFamily: font.value || 'inherit' }}>
                  {font.label}
                </option>
              ))}
            </select>
          </div>

          {/* Custom CSS */}
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>{t('quote_builder_widget.custom_css')}</label>
            <p style={helpTextStyle}>{t('quote_builder_widget.custom_css_info')}</p>
            <button
              type="button"
              className="btn simple-btn"
              onClick={onOpenCssEditor}
            >
              <i className="fa fa-code" aria-hidden="true"></i>{' '}
              {customCss ? t('quote_builder_widget.edit_css') : t('quote_builder_widget.add_custom_css')}
            </button>
            {customCss && (
              <span style={{ marginLeft: '10px', color: '#28a745' }}>
                <i className="fa fa-check-circle" aria-hidden="true"></i>{' '}
                {t('quote_builder_widget.custom_css_applied')}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AppearanceSection;
